import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CheckIcon from '@mui/icons-material/Check';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Box, Container, Link, Paper, Step, StepLabel, Stepper, Typography } from '@mui/material';

import { CURRENCY_SIGNS, STATUS } from '~/literals';

import FlightItineraryTripDetails, {
  ItineraryDetailsStep,
} from '~/components/FlightItineraryTripDetails';
import ItineraryFinalStage from '~/components/ItineraryFinalStage';
import ItineraryPayment from '~/components/ItineraryPayment';

import { currencySelector, selectItineraryPackages } from '~/selectors';
import { getItineraryPackages } from '~/store/slices/itinerary-packages';
import {
  requestItineraryPayment,
  setSelectedPackageId,
  setWizardStep,
} from '~/store/slices/itinerary-wizard';
import { RootState } from '~/types';
import { trackButtonClick } from '~/utils/analytics';
import { scrollToWizardProgress } from '~/utils/wizard-scroll';

import { MenuItemKey } from './HeroMenuButtons';
import ItineraryPackages, { PackageDef } from './ItineraryPackages';

type Props = {
  initialStep?: number;
  initialPackageId?: string;
  paymentResult?: 'success' | 'failed' | 'canceled';
  menuItems?: MenuItemKey[];
};

const WIZARD_STEP_LABELS = ['Select package', 'Flight details', 'Pay', 'Completed'];

const FlightItineraryRequestWizard: React.FC<Props> = ({
  initialPackageId,
  initialStep = 1,
  menuItems: _menuItems,
  paymentResult,
}) => {
  const dispatch = useDispatch();
  const selectedPackageId = useSelector(
    (state: RootState) => state.itineraryWizard.selectedPackageId,
  );
  const selectedCurrency = useSelector(currencySelector);
  const { packages, pageStatus } = useSelector(selectItineraryPackages);
  const [step, setStep] = useState<number>(initialStep ?? 1);
  const [detailsStep, setDetailsStep] = useState<ItineraryDetailsStep>('search');
  const [selectedPackage, setSelectedPackage] = useState<PackageDef | null>(null);
  const wizardContentRef = useRef<HTMLDivElement>(null);
  const stepperRef = useRef<HTMLDivElement>(null);
  const previousDetailsStepRef = useRef(detailsStep);
  const activeStep = Math.min(Math.max(step - 1, 0), 3);

  useEffect(() => {
    if (step !== 2) {
      return () => undefined;
    }

    const animationFrame = requestAnimationFrame(() => {
      scrollToWizardProgress(stepperRef.current);
    });

    return () => cancelAnimationFrame(animationFrame);
  }, [step]);

  useEffect(() => {
    if (previousDetailsStepRef.current === detailsStep) {
      return;
    }

    previousDetailsStepRef.current = detailsStep;
    const animationFrame = requestAnimationFrame(() => {
      scrollToWizardProgress(stepperRef.current);
    });

    return () => cancelAnimationFrame(animationFrame);
  }, [detailsStep]);

  useEffect(() => {
    if (pageStatus === STATUS.IDLE) {
      dispatch(getItineraryPackages());
    }
  }, [dispatch, pageStatus]);

  useEffect(() => {
    const resolvedPackageId = selectedPackageId || initialPackageId;

    if (resolvedPackageId) {
      const p = packages.find(x => x.id === resolvedPackageId) || null;

      setSelectedPackage(p);
    } else {
      setSelectedPackage(null);
    }

    setStep(initialStep ?? 1);
  }, [initialStep, initialPackageId, packages, selectedPackageId]);

  const handlePaymentRequested = () => {
    trackButtonClick('FlightItineraryRequestWizard', 'Request payment');
    dispatch(setWizardStep(3));
    dispatch(requestItineraryPayment());
    setStep(3);
  };

  return (
    <Box
      sx={{
        bgcolor: '#fff',
        minHeight: { xs: 'auto', md: 'calc(100vh - 220px)' },
        py: 0,
        display: 'flex',
      }}
    >
      <Container maxWidth="xl" sx={{ py: 0, display: 'flex', flex: 1 }}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 0,
            border: 'none',
            overflow: 'hidden',
            bgcolor: 'background.paper',
            width: '100%',
            display: 'flex',
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '320px minmax(0, 1fr)' },
              minHeight: '100%',
              width: '100%',
            }}
          >
            <Box
              sx={{
                background: {
                  xs: '#0b1f44',
                  md: 'linear-gradient(180deg, #365ca3 0%, #92addd 55%, #f4f7ff 100%)',
                },
                color: '#fff',
                mt: { xs: 3, md: 0 },
                p: { xs: 2.5, md: 3 },
                borderRight: { md: '1px solid rgba(255,255,255,0.12)' },
              }}
            >
              <Typography
                sx={{ color: 'rgba(255,255,255,0.75)', letterSpacing: 1 }}
                variant="overline"
              >
                Selected package
              </Typography>
              <Typography sx={{ mb: 2, fontWeight: 700 }} variant="h6">
                {selectedPackage?.title ?? 'No package selected yet'}
              </Typography>

              {selectedPackage ? (
                <>
                  {!!selectedPackage.price && (
                    <Typography sx={{ fontWeight: 700, mb: 1.5 }} variant="subtitle1">
                      Price: {CURRENCY_SIGNS[selectedCurrency]}
                      {selectedPackage.price[selectedCurrency]} {selectedCurrency}
                    </Typography>
                  )}

                  <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                    <Typography sx={{ mb: 1 }} variant="subtitle2">
                      Features
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                      {selectedPackage.features.map((feature, index) => (
                        <Box
                          key={`${selectedPackage.id}-feature-${index}`}
                          sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}
                        >
                          <CheckIcon sx={{ color: '#fff', fontSize: 18, mt: 0.25 }} />
                          <Typography variant="body2">{feature}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </>
              ) : (
                <Typography sx={{ color: 'rgba(255,255,255,0.8)' }} variant="body2">
                  Select a package on the right to preview its pricing and included features here.
                </Typography>
              )}
            </Box>

            <Box
              ref={wizardContentRef}
              sx={{ p: { xs: 2, md: 3 }, pt: { xs: 6, md: 8 }, mb: { xs: 6, md: 14 } }}
            >
              <Stepper ref={stepperRef} activeStep={activeStep} alternativeLabel sx={{ mb: 8 }}>
                {WIZARD_STEP_LABELS.map(label => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>

              {step === 1 ? (
                <Box>
                  <Typography gutterBottom sx={{ mb: 2 }} variant="h5">
                    Choose a package
                  </Typography>
                  <ItineraryPackages
                    onSelect={p => {
                      setSelectedPackage(p as PackageDef);
                      setDetailsStep('search');
                      dispatch(setSelectedPackageId((p as PackageDef).id));
                      dispatch(setWizardStep(2));
                      setStep(2);
                    }}
                  />
                </Box>
              ) : step === 2 ? (
                <Box>
                  <Box
                    sx={{
                      mb: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 2,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Box>
                      <Typography variant="h6">
                        Selected package: {selectedPackage?.title}
                      </Typography>
                      {selectedPackage?.price?.[selectedCurrency] !== undefined ? (
                        <Typography color="text.secondary" variant="body2">
                          Price: {CURRENCY_SIGNS[selectedCurrency]}
                          {selectedPackage.price[selectedCurrency]} {selectedCurrency}
                        </Typography>
                      ) : null}
                    </Box>
                    <Link
                      component="button"
                      onClick={() => {
                        trackButtonClick('FlightItineraryRequestWizard', 'Change package');
                        setSelectedPackage(null);
                        dispatch(setSelectedPackageId(null));
                        dispatch(setWizardStep(1));
                        setStep(1);
                      }}
                      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}
                      underline="hover"
                    >
                      <EditOutlinedIcon fontSize="small" />
                      Change package
                    </Link>
                  </Box>

                  <FlightItineraryTripDetails
                    currentStep={detailsStep}
                    onPaymentRequested={handlePaymentRequested}
                    onStepChange={setDetailsStep}
                    selectedPackage={selectedPackage}
                  />
                </Box>
              ) : step === 3 ? (
                <ItineraryPayment />
              ) : (
                <ItineraryFinalStage paymentResult={paymentResult} />
              )}
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default FlightItineraryRequestWizard;
